import { webDataNotAsked } from "@lvt/web-data";
import { Typography, IconButton, Link, Menu, MenuItem } from "@material-ui/core";
import { MoreVert, ArrowDropUp } from "@material-ui/icons";
import { DateTime } from "luxon";
import { useContext, useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";

import {
    useStyles,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@/common/components/ArchivePage/CameraSection/CameraSectionStyles";
import PresetComponent from "@/common/components/ArchivePage/Preset/Preset";
import { PresetImagesContext } from "@/common/contexts/PresetImagesContext";
import { fetchPresetImages, PresetFetchParams } from "@/store/archives/async";
import { retrieveThumbnails } from "@/store/archives/selector";
import { RootState, useSelector } from "@/store/index";
import { debounce } from "@/utilities/async";
import { Preset, ArchivedVideo, PresetImages, BucketImages } from "@/utilities/types/archive";

import { getExpandedPresetsSet } from "./CameraSection.utils";


interface CameraSectionProps {
    id: number;
    name: string;
    presets: Array<Preset>;
    videos: Array<ArchivedVideo>;
    notRecordingReason: string | null;
    canStream: boolean;
}

export default function CameraSection({
    id,
    name,
    presets,
    canStream,
}: CameraSectionProps) {
    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const classes = useStyles();

    const handleVisibility = () => setIsExpanded((prev) => !prev);
    const handleMenuClose = () => setMenuAnchor(null);

    const accordionWidth: number = useSelector((state: RootState) => state.archives.gridAndAccordionWidth);
    const timelineStart: string = useSelector((state: RootState) => state.archives.timelineStart);
    const timeSpan: number = useSelector((state: RootState) => state.archives.timeSpan);

    const timelineStartTime = DateTime.fromISO(timelineStart);
    const startTime = timelineStartTime.minus({ minutes: 10 });
    const endTime = timelineStartTime.plus({ seconds: timeSpan + 10 * 60 });
    const thumbnails: BucketImages = useSelector(
        retrieveThumbnails(
            presets.map((preset) => preset.id),
            startTime,
            endTime,
        ),
    );

    const presetImagesService = useContext(PresetImagesContext);

    const presetImageFetchDebounce = useMemo(() => debounce((state: PresetFetchParams) => {
        dispatch(fetchPresetImages(state));
    }, 200),[presetImagesService, presets, startTime, endTime ]);


    useEffect(() => {
        presetImageFetchDebounce({ presetImagesService, presets, startTime, endTime });
    }, [timelineStart, timeSpan]);


    // All the presets that should start expanded
    const expandedPresets = getExpandedPresetsSet({ presets });

    return (
        <Accordion key={id} expanded={isExpanded} className={classes.section} style={{ width: accordionWidth }}>
            <AccordionSummary>
                <IconButton onClick={handleVisibility}>
                    <ArrowDropUp className={ isExpanded ? classes.btnOpen : classes.btnClosed }/>
                </IconButton>
                <Typography variant="subtitle1" className={classes.cameraName} onClick={handleVisibility}>
                    {name}
                </Typography>

                {canStream ? (
                    <>
                        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                            <MoreVert />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} keepMounted open={Boolean(menuAnchor)} onClose={handleMenuClose}>
                            <Link href={`/camera/${id}/stream`} underline="none" color="textPrimary">
                                <MenuItem>Stream Camera</MenuItem>
                            </Link>
                        </Menu>
                    </>
                ) : (
                    <></>
                )}
            </AccordionSummary>
            <AccordionDetails>
                {presets.map((preset, index) => {
                    const initPresetImage: PresetImages = {};
                    const presetThumbnails: PresetImages = Object.entries(thumbnails).reduce(
                        (sofar, [startTime, images]) => {
                            if (images[preset.id]) {
                                sofar[parseInt(startTime, 10)] = images[preset.id];
                            } else {
                                sofar[parseInt(startTime, 10)] = webDataNotAsked();
                            }

                            return sofar;
                        },
                        initPresetImage,
                    );

                    return (
                        <PresetComponent
                            cameraName={name}
                            id={preset.id}
                            images={presetThumbnails}
                            isDefault={preset.default}
                            key={preset.id}
                            presetName={preset.name}
                            startExpanded={expandedPresets.has(index)}
                        />
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );
}
